import React, { useState, useRef, useEffect } from "react";
import { X, Send, Phone, Calendar, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";

const GHOST_LOGO = "/images/ghost-logo.png";

const GhostAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState(() => `ghost-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Load greeting on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: "ghost",
        content: "Welcome to New Leaf PMC. I'm Ghost, your project estimator. What kind of work do you need done? I handle drywall, painting, flooring, doors, windows, tile, roofing, cabinets, and more."
      }]);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    const userMsg = text.trim();
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setInputValue("");
    setIsTyping(true);

    try {
      const res = await axios.post(`${API}/ghost-chat`, {
        session_id: sessionId,
        message: userMsg
      });
      setMessages(prev => [...prev, { role: "ghost", content: res.data.response }]);
    } catch (err) {
      // Offline fallback
      setMessages(prev => [...prev, {
        role: "ghost",
        content: "I'm having trouble connecting. You can reach us directly at 863-340-9244 or try again in a moment."
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSend = () => sendMessage(inputValue);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickAction = (action) => {
    if (action === "estimate") {
      sendMessage("I want to schedule a free estimate");
    } else if (action === "call") {
      window.location.href = "tel:863-340-9244";
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 md:bottom-20 right-4 z-40 w-14 h-14 rounded-full shadow-lg transition-all duration-300 overflow-hidden ${
          isOpen ? "scale-0 opacity-0" : "scale-100 opacity-100"
        } animate-pulse-glow`}
        data-testid="ghost-chat-btn"
        style={{ background: "#15161C", border: "2px solid #FF6B35" }}
      >
        <img src={GHOST_LOGO} alt="Ghost" className="w-full h-full object-cover" />
      </button>

      {/* Chat Panel */}
      <div
        className={`fixed bottom-24 md:bottom-20 right-4 z-50 w-[calc(100%-2rem)] max-w-[400px] transition-all duration-300 ${
          isOpen ? "scale-100 opacity-100" : "scale-0 opacity-0 pointer-events-none"
        }`}
        style={{ transformOrigin: "bottom right" }}
        data-testid="ghost-chat-panel"
      >
        <div className="bg-[#15161C] border border-[#2A2B32] rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: "520px" }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#4A2B6E]/50 via-[#0D5C63]/50 to-[#FF6B35]/50 border-b border-[#2A2B32] shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#FF6B35]">
                <img src={GHOST_LOGO} alt="Ghost" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">GHOST</p>
                <div className="flex items-center space-x-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  <p className="text-green-400 text-xs">Online - AI Sales Assistant</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-full hover:bg-white/10 text-[#A0AAB2] hover:text-white transition-colors"
              data-testid="ghost-close-btn"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: "280px", maxHeight: "340px" }}>
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "ghost" && (
                  <div className="w-6 h-6 rounded-full overflow-hidden border border-[#2A2B32] mr-2 shrink-0 mt-1">
                    <img src={GHOST_LOGO} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-[#FF6B35] text-white rounded-br-sm"
                    : "bg-[#1F2027] text-white border border-[#2A2B32] rounded-bl-sm"
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="w-6 h-6 rounded-full overflow-hidden border border-[#2A2B32] mr-2 shrink-0 mt-1">
                  <img src={GHOST_LOGO} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="bg-[#1F2027] border border-[#2A2B32] rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex space-x-1.5">
                    <span className="w-2 h-2 bg-[#A0AAB2] rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                    <span className="w-2 h-2 bg-[#A0AAB2] rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                    <span className="w-2 h-2 bg-[#A0AAB2] rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="px-3 py-2 border-t border-[#2A2B32] flex space-x-2 shrink-0">
            <button
              onClick={() => handleQuickAction("estimate")}
              className="flex-1 flex items-center justify-center space-x-1.5 py-2 px-3 bg-[#FF6B35] text-white text-xs font-semibold rounded-lg hover:bg-[#FF6B35]/90 transition-colors"
              data-testid="ghost-quick-book"
            >
              <Calendar size={14} />
              <span>Book Free Estimate</span>
            </button>
            <button
              onClick={() => handleQuickAction("call")}
              className="flex items-center justify-center space-x-1.5 py-2 px-3 bg-[#1F2027] border border-[#2A2B32] text-white text-xs font-semibold rounded-lg hover:border-[#FF6B35] transition-colors"
              data-testid="ghost-quick-call"
            >
              <Phone size={14} />
              <span>Call Now</span>
            </button>
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[#2A2B32] shrink-0">
            <div className="flex items-center space-x-2">
              <input
                ref={inputRef}
                type="text"
                placeholder="Describe your project..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 px-4 py-2.5 bg-[#0B0C10] border border-[#2A2B32] rounded-full text-white text-sm placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                data-testid="ghost-input"
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || isTyping}
                className="p-2.5 rounded-full gradient-btn disabled:opacity-50"
                data-testid="ghost-send-btn"
              >
                <Send size={18} />
              </button>
            </div>
            <p className="text-center text-[#A0AAB2]/30 text-[10px] mt-2" data-testid="ghost-branding">Powered by Prestigious Companies</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default GhostAssistant;
